import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { corpusService } from '../../services/corpusService';
import { glossaryService, GlossaryTermWithSector } from '../../services/glossaryService';
import { Card, CardContent, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faFileImport, faFileExport, faSearch } from '@fortawesome/free-solid-svg-icons';
import TermsTable from '../../components/dictionaries/TermsTable';
import AddTermModal from '../../components/dictionaries/AddTermModal';
import EditTermModal from '../../components/dictionaries/EditTermModal';
import ImportTermsModal from '../../components/dictionaries/ImportTermsModal';
import toast from 'react-hot-toast';

export default function DictionariesPage() {
  const queryClient = useQueryClient();
  const [selectedSectorId, setSelectedSectorId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLevel, setSelectedLevel] = useState<'B2' | 'C1' | 'C2' | null>(null);
  const [currentPageBySector, setCurrentPageBySector] = useState<Record<number, number>>({});
  const [termsBySector, setTermsBySector] = useState<Record<number, GlossaryTermWithSector[]>>({});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState<GlossaryTermWithSector | null>(null);

  // Get current page for selected sector
  const currentPage = selectedSectorId ? (currentPageBySector[selectedSectorId] || 1) : 1;
  const allTerms = selectedSectorId ? (termsBySector[selectedSectorId] || []) : [];

  // Fetch sectors
  const { data: sectorsData, isLoading: sectorsLoading } = useQuery({
    queryKey: ['sectors'],
    queryFn: () => corpusService.getSectors(),
  });

  // Fetch terms for selected sector with pagination
  const { data: termsData, isLoading: termsLoading, isFetching: termsFetching } = useQuery({
    queryKey: ['glossary-terms', selectedSectorId, searchQuery, selectedLevel, currentPage],
    queryFn: () => {
      if (!selectedSectorId) return null;
      return glossaryService.getTermsBySector(selectedSectorId, {
        search: searchQuery || undefined,
        mcerlLevel: selectedLevel || undefined,
        page: currentPage,
        limit: 100,
      });
    },
    enabled: !!selectedSectorId,
    placeholderData: (previousData) => previousData,
  });

  // Update terms for the selected sector when new data arrives
  useEffect(() => {
    if (termsData?.terms && selectedSectorId) {
      setTermsBySector(prev => {
        const currentPageForSector = currentPageBySector[selectedSectorId] || 1;
        if (currentPageForSector === 1) {
          // First page: replace terms for this sector
          return { ...prev, [selectedSectorId]: termsData.terms };
        } else {
          // Subsequent pages: append terms for this sector
          const existingTerms = prev[selectedSectorId] || [];
          return { ...prev, [selectedSectorId]: [...existingTerms, ...termsData.terms] };
        }
      });
    }
  }, [termsData, selectedSectorId, currentPageBySector]);

  // Reset to page 1 when sector, search, or level changes
  useEffect(() => {
    if (selectedSectorId) {
      setCurrentPageBySector(prev => ({ ...prev, [selectedSectorId]: 1 }));
      // Clear this sector's terms when searching or filtering
      if (searchQuery || selectedLevel) {
        setTermsBySector(prev => ({ ...prev, [selectedSectorId]: [] }));
      }
    }
  }, [selectedSectorId, searchQuery, selectedLevel]);

  // Fetch statistics
  const { data: statsData } = useQuery({
    queryKey: ['glossary-stats'],
    queryFn: () => glossaryService.getStats(),
  });

  // Set initial sector when data loads
  useEffect(() => {
    if (sectorsData?.sectors && sectorsData.sectors.length > 0 && !selectedSectorId) {
      // Find General sector or use first sector
      const generalSector = sectorsData.sectors.find(s => s.code === 'GENERAL');
      setSelectedSectorId(generalSector?.id || sectorsData.sectors[0].id);
    }
  }, [sectorsData, selectedSectorId]);

  // Delete term mutation
  const deleteMutation = useMutation({
    mutationFn: (termId: number) => glossaryService.deleteTerm(termId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['glossary-terms'] });
      queryClient.invalidateQueries({ queryKey: ['glossary-stats'] });
      toast.success('Término eliminado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error al eliminar término');
    },
  });

  // Export terms
  const handleExport = async (format: 'json' | 'txt') => {
    try {
      const blob = await glossaryService.exportTerms({
        sectorId: selectedSectorId || undefined,
        format,
      });
      const filename = `glosario_${selectedSectorId || 'todos'}_${Date.now()}.${format}`;
      glossaryService.downloadExportedFile(blob, filename);
      toast.success('Glosario exportado exitosamente');
    } catch (error: any) {
      toast.error('Error al exportar glosario');
    }
  };

  const handleEdit = (term: GlossaryTermWithSector) => {
    setSelectedTerm(term);
    setIsEditModalOpen(true);
  };

  const handleDelete = (termId: number) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este término?')) {
      deleteMutation.mutate(termId);
    }
  };

  const handleLoadMore = () => {
    if (selectedSectorId) {
      setCurrentPageBySector(prev => ({
        ...prev,
        [selectedSectorId]: (prev[selectedSectorId] || 1) + 1
      }));
    }
  };

  if (sectorsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Cargando sectores...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-8 shadow-lg">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2 text-white">Gestión de Glosarios</h1>
            <p className="text-white/90">
              {statsData?.stats.total || 0} términos de glosario en total
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={() => setIsImportModalOpen(true)}
              className="bg-white/20 text-white border-white/30 hover:bg-white/30"
            >
              <FontAwesomeIcon icon={faFileImport} className="mr-2" />
              Importar
            </Button>
            <Button
              onClick={() => handleExport('json')}
              className="bg-white/20 text-white border-white/30 hover:bg-white/30"
            >
              <FontAwesomeIcon icon={faFileExport} className="mr-2" />
              Exportar JSON
            </Button>
            <Button
              onClick={() => handleExport('txt')}
              className="bg-white/20 text-white border-white/30 hover:bg-white/30"
            >
              <FontAwesomeIcon icon={faFileExport} className="mr-2" />
              Exportar TXT
            </Button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      {statsData && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <p className="text-gray-500 text-sm mb-1">Total de Términos</p>
                <p className="text-3xl font-bold text-blue-600">{statsData.stats.total}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <p className="text-gray-500 text-sm mb-1">Prioridad Alta</p>
                <p className="text-3xl font-bold text-red-600">
                  {statsData.stats.byPriority.find((p) => p.priority === 'ALTA')?.count || 0}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <p className="text-gray-500 text-sm mb-1">Sectores Activos</p>
                <p className="text-3xl font-bold text-green-600">
                  {statsData.stats.bySector.length}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <Card>
        <CardHeader className="border-b">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <h2 className="text-xl font-semibold text-gray-800">Términos por Sector</h2>
            <div className="flex gap-2 items-center flex-wrap">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar términos..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
              </div>
              <Button onClick={() => setIsAddModalOpen(true)} className="bg-blue-600 hover:bg-blue-700">
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Añadir Término
              </Button>
            </div>
          </div>

          {/* MCERL Level Filter */}
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-700">Filtrar por Nivel MCERL:</span>
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant={selectedLevel === null ? 'default' : 'outline'}
                  onClick={() => setSelectedLevel(null)}
                  className={selectedLevel === null ? 'bg-blue-600 hover:bg-blue-700' : ''}
                >
                  Todos
                </Button>
                {['B2', 'C1', 'C2'].map((level) => (
                  <Button
                    key={level}
                    size="sm"
                    variant={selectedLevel === level ? 'default' : 'outline'}
                    onClick={() => setSelectedLevel(level as 'B2' | 'C1' | 'C2')}
                    className={
                      selectedLevel === level
                        ? level === 'B2'
                          ? 'bg-blue-600 hover:bg-blue-700'
                          : 'bg-orange-600 hover:bg-orange-700'
                        : level === 'B2'
                        ? 'border-blue-300 text-blue-700 hover:bg-blue-50'
                        : 'border-orange-300 text-orange-700 hover:bg-orange-50'
                    }
                  >
                    {level}
                  </Button>
                ))}
              </div>
              {selectedLevel && (
                <span className="text-sm text-gray-500 ml-2">
                  Mostrando solo términos de nivel {selectedLevel}
                </span>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {sectorsData?.sectors && sectorsData.sectors.length > 0 ? (
            <Tabs
              value={selectedSectorId?.toString() || sectorsData.sectors[0].id.toString()}
              onValueChange={(value) => setSelectedSectorId(Number(value))}
            >
              <TabsList className="w-full mb-6 flex-wrap h-auto">
                {sectorsData.sectors
                  .slice()
                  .sort((a, b) => {
                    if (a.code === 'GENERAL') return -1;
                    if (b.code === 'GENERAL') return 1;
                    return a.name.localeCompare(b.name);
                  })
                  .map((sector) => (
                    <TabsTrigger
                      key={sector.id}
                      value={sector.id.toString()}
                      className="flex items-center gap-2"
                    >
                      {sector.icon && <span>{sector.icon}</span>}
                      <span>{sector.name}</span>
                      <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                        {statsData?.stats.bySector.find((s) => s.sector.id === sector.id)?.count || 0}
                      </span>
                    </TabsTrigger>
                  ))}
              </TabsList>

              {sectorsData.sectors.map((sector) => (
                <TabsContent key={sector.id} value={sector.id.toString()}>
                  {(termsLoading && currentPage === 1) || allTerms.length === 0 && termsLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-500">Cargando términos...</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <TermsTable
                        terms={allTerms}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                      />

                      {/* Load More Button */}
                      {termsData && termsData.hasMore && allTerms.length > 0 && (
                        <div className="flex justify-center mt-6">
                          <Button
                            onClick={handleLoadMore}
                            disabled={termsFetching}
                            className="bg-blue-600 hover:bg-blue-700 px-8"
                          >
                            {termsFetching ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Cargando...
                              </>
                            ) : (
                              <>
                                Cargar más ({termsData.total - allTerms.length} restantes)
                              </>
                            )}
                          </Button>
                        </div>
                      )}

                      {/* Stats */}
                      {allTerms.length > 0 && termsData && (
                        <div className="mt-4 text-center text-sm text-gray-500">
                          Mostrando {allTerms.length} de {termsData.total} términos
                        </div>
                      )}
                    </>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">No hay sectores disponibles</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <AddTermModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        sectors={sectorsData?.sectors || []}
        defaultSectorId={selectedSectorId || undefined}
      />

      {selectedTerm && (
        <EditTermModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedTerm(null);
          }}
          term={selectedTerm}
          sectors={sectorsData?.sectors || []}
        />
      )}

      <ImportTermsModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        sectors={sectorsData?.sectors || []}
        defaultSectorId={selectedSectorId || undefined}
      />
    </div>
  );
}
